import useConfirmDialog from '@/composables/confirm-dialog';
import useGoalSettings from '@/composables/logged-in-user-account/goal-settings';
import useGoals from '@/composables/goal/goals';
import useSnackBar from '@/composables/snackbar';
import useTrash from '@/composables/trash/trash';
import { EVENTS } from '@/lib/constants';
import { EventBus } from '@/lib/event-bus';
import { logCatch } from '@/lib/logger/logger';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

export default function useDeleteGoal() {
  const deleteLoading = ref(false);
  const { t } = useI18n();
  const { goalSettings } = useGoalSettings();
  const { deleteMultiple } = useGoals();
  const { confirm } = useConfirmDialog();
  const { success, error } = useSnackBar();
  const { restoreMultipleSoftDeleted } = useTrash({ model: ref('goal') });
  const router = useRouter();
  const showDeleteGoalModal = ({
    goals,
    redirect,
    title = t('goal.detailHeader.deletePrompt', { title: goalSettings.value.featureNamePlural }),
    onCancel = () => { },
    okText = t('general.yesDelete'),
    cancelText = t('general.cancel'),
    hardDelete = false,
  }) => new Promise((resolve) => {
    confirm({
      title,
      okText,
      okType: 'danger',
      cancelText,
      maskClosable: true,
      onCancel: () => {
        onCancel();
        resolve();
      },
      async onOk() {
        await deleteGoalEntities({ goals, redirect, hardDelete });
        resolve({ success: true });
      },
    });
  });
  const deleteGoalEntities = ({ goals, redirect, hardDelete }) => {
    deleteLoading.value = true;
    const deletedIds = goals.map((g) => g.uid);

    return deleteMultiple(
      deletedIds,
      { softDelete: !hardDelete },
    ).then(async () => {
      EventBus.$emit(EVENTS.GOAL.GOALS_DELETED, { goals });

      if (hardDelete) {
        // No undo for hard delete
        success(t('success.deleted'));
      } else {
        // Add undo action for soft delete
        success(t('success.movedToTrash'), {
          action: {
            title: t('general.undo'),
            handler: () => restoreMultipleSoftDeleted(deletedIds)
              .then(() => success(t('success.restored')))
              .catch(() => error()),
          },
        });
      }

      await redirectFn(redirect);
    }).catch(logCatch(() => {
      error();
    })).finally(() => {
      deleteLoading.value = false;
    });
  };
  const redirectFn = async (redirect) => {
    if (redirect !== undefined && Object.keys(redirect).length > 0) {
      return router.push(redirect);
    }
    return undefined;
  };

  return {
    showDeleteGoalModal,
    deleteLoading,
  };
}
